import moment from 'moment';

import { formatEntityId } from '../../../../helpers';
import { ENTITY_PREFIX } from '../../../../constants';
import { ACTIVATION_DATE_FORMAT } from './constants';

import type { ActivationLimit, ActivationPeriod, TPActivationItem } from './TPActivationChart';
import type { Moment } from 'moment';
import type { ITreatmentPlan } from '../../../../utils/types';

function getActivationPeriodData(
  logs: Array<{ id: number; previous_value: boolean; new_value: boolean; date_changed: string }>,
) {
  const usedIds = new Set<number>();
  const result: Array<{
    isActive: boolean;
    activationStartId: number;
    activationEndId: number | null;
    activationStart: string;
    activationEnd: string | null;
  }> = [];

  // Sort logs by date_changed ascending
  const sortedLogs = [...logs].sort(
    (a, b) => new Date(a.date_changed).getTime() - new Date(b.date_changed).getTime(),
  );

  for (let i = 0; i < sortedLogs.length; i++) {
    const startLog = sortedLogs[i];
    if (startLog.new_value === true && !usedIds.has(startLog.id)) {
      // Find the closest new_value=false after this date
      const endLog = sortedLogs.find(
        (log) =>
          log.new_value === false &&
          !usedIds.has(log.id) &&
          new Date(log.date_changed) > new Date(startLog.date_changed),
      );
      if (endLog) {
        result.push({
          isActive: false,
          activationStartId: startLog.id,
          activationEndId: endLog.id,
          activationStart: startLog.date_changed,
          activationEnd: endLog.date_changed,
        });
        usedIds.add(startLog.id);
        usedIds.add(endLog.id);
      } else {
        // No matching end found, so it's still active
        result.push({
          isActive: true,
          activationStartId: startLog.id,
          activationEndId: null,
          activationStart: startLog.date_changed,
          activationEnd: null,
        });
        usedIds.add(startLog.id);
      }
    }
  }
  return result;
}

export function transformActivationData(tpList: ITreatmentPlan[]) {
  // SAFETY: Treatment-plan records are normalized before the activation chart receives them.
  return tpList.map((tp) => ({
    tpId: tp.id as number,
    isActive: tp.is_active as boolean,
    statusId: tp.status_id as number,
    status: tp.status?.label ?? '',
    startDate: tp.date_treatment_plan_started as string,
    activationsPeriods: getActivationPeriodData(tp.activation_logs || []),
  }));
}

export function getTimeScaleUnits(startDate: string, endDate: string) {
  // Returns array of date strings for the time scale
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const diffMonths =
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  const diffYears = end.getFullYear() - start.getFullYear();
  let unit = 'month';
  let step = 1;
  let format = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (diffDays <= 14) {
    unit = 'day';
    step = 1;
    format = (d: Date) => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
  } else if (diffDays <= 31) {
    unit = 'week';
    step = 7;
    format = (d: Date) => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
  } else if (diffDays > 31 && diffMonths <= 9 && diffYears < 2) {
    unit = 'month';
    step = 1;
    format = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } else if (diffMonths > 9 && diffYears < 2) {
    unit = 'month';
    step = 3;
    format = (d: Date) => {
      const month = d.toLocaleDateString('en-US', { month: 'short' });
      return `${month} ${d.getFullYear()}`;
    };
  } else if (diffYears >= 2) {
    unit = 'month';
    step = 6;
    format = (d: Date) => {
      const month = d.getMonth() < 6 ? 'Jan' : 'Jul';
      return `${month} ${d.getFullYear()}`;
    };
  }
  const scale: string[] = [];
  const current = new Date(start);
  while (current <= end) {
    scale.push(format(current));
    if (unit === 'day') {
      current.setDate(current.getDate() + step);
    } else if (unit === 'week') {
      current.setDate(current.getDate() + step);
    } else {
      current.setMonth(current.getMonth() + step);
    }
  }
  return scale;
}

export function getDateDiffPercent(
  startDate: string,
  endDate: string,
  from: string,
  to: string | null,
) {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const fromTime = new Date(from).getTime();
  const toTime = to ? new Date(to).getTime() : end;
  const total = end - start;
  const left = ((fromTime - start) / total) * 100;
  const width = ((toTime - fromTime) / total) * 100;
  return { left, width };
}

export function getActivationLogStartDateChangeLimit(
  tpActivationData: TPActivationItem[],
  selectedTpId: number,
  selectedActivationPeriod: ActivationPeriod,
) {
  // Find the TP item for selectedTpId
  const selectedTP = tpActivationData.find((tp) => tp.tpId === selectedTpId);
  const activationStart = moment(selectedActivationPeriod.activationStart);

  // Collect all activationEnd dates before activationStart using moment, and keep reference to tpId
  const pastActivationEnds: Array<{ date: string; tpId: number }> = [];
  tpActivationData.forEach((tp) => {
    tp.activationsPeriods.forEach((period) => {
      if (period.activationEnd && moment(period.activationEnd).isBefore(activationStart)) {
        pastActivationEnds.push({ date: period.activationEnd, tpId: tp.tpId });
      }
    });
  });

  const selectedTPStartDate = selectedTP?.startDate ?? null;

  // Find the latest date and its reason
  let latestDate: string | null = null;
  let reason: string | null = null;

  if (pastActivationEnds.length === 0 && !selectedTPStartDate) {
    return { date: null, reason: null };
  }

  // Find the latest activationEnd
  let latestActivationEnd: { date: string; tpId: number } | null = null;
  if (pastActivationEnds.length > 0) {
    latestActivationEnd = pastActivationEnds.reduce((latest, item) =>
      moment(item.date).isAfter(moment(latest.date)) ? item : latest,
    );
  }

  // Compare latestActivationEnd and selectedTPStartDate
  if (latestActivationEnd && selectedTPStartDate) {
    if (moment(selectedTPStartDate).isAfter(moment(latestActivationEnd.date))) {
      latestDate = selectedTPStartDate;
      reason = 'Activation start date cannot be earlier than the treatment plan start date';
    } else {
      latestDate = latestActivationEnd.date;
      reason = `TP with id ${formatEntityId(
        latestActivationEnd.tpId,
        ENTITY_PREFIX.TreatmentPlan,
      )} was active till ${moment(latestActivationEnd.date).format(ACTIVATION_DATE_FORMAT)}`;
    }
  } else if (selectedTPStartDate) {
    latestDate = selectedTPStartDate;
    reason = 'Activation start date cannot be earlier than the treatment plan start date';
  }

  return { date: moment(latestDate).add(1, 'minute').toISOString(), reason };
}

export function getActivationLogEndDateChangeLimit(
  tpActivationData: TPActivationItem[],
  selectedTpId: number,
  selectedActivationPeriod: ActivationPeriod,
) {
  const activationEnd = selectedActivationPeriod.activationEnd;

  if (!activationEnd) {
    return {
      date: null,
      reason: `TP ${formatEntityId(selectedTpId, ENTITY_PREFIX.TreatmentPlan)} is active currently`,
    };
  }

  const activationEndMoment = moment(activationEnd);

  // Find all activationStart dates after activationEnd
  const futureActivationStarts: Array<{ date: string; tpId: number }> = [];
  tpActivationData.forEach((tp) => {
    tp.activationsPeriods.forEach((period) => {
      if (period.activationStart && moment(period.activationStart).isAfter(activationEndMoment)) {
        futureActivationStarts.push({ date: period.activationStart, tpId: tp.tpId });
      }
    });
  });

  if (futureActivationStarts.length === 0) {
    return {
      date: moment().subtract(1, 'minute').toISOString(),
      reason: 'Can be set up to the current time.',
    };
  }

  // Find the earliest activationStart
  const earliestActivationStart = futureActivationStarts.reduce((earliest, item) =>
    moment(item.date).isBefore(moment(earliest.date)) ? item : earliest,
  );

  return {
    date: moment(earliestActivationStart.date).subtract(1, 'minute').toISOString(),
    reason: `TP with id ${formatEntityId(
      earliestActivationStart.tpId,
      ENTITY_PREFIX.TreatmentPlan,
    )} becomes active from ${moment(earliestActivationStart.date).format(ACTIVATION_DATE_FORMAT)}`,
  };
}

export function formatActivationErrorMessage(errorMessage: string) {
  if (!errorMessage.includes('Validation failed - ') || !errorMessage.includes('DATE:'))
    return errorMessage;
  const errMessage = errorMessage.replace('Validation failed - ', '');
  const [err1, err2] = errMessage.split('DATE:');
  const date = moment.utc(err2.trim()).local().format('Do MMM, YYYY, h:mm a');
  const error = err1.replace('{DATE}', date);
  return error;
}

export const disableActivationStartDate = (
  current: Moment | undefined,
  activationStartDateLimit: ActivationLimit,
  selectedActivationPeriod: ActivationPeriod,
  endDate: Moment | null,
) => {
  const minDate = activationStartDateLimit.date ? moment(activationStartDateLimit.date) : null;
  const maxDate =
    endDate || selectedActivationPeriod?.activationEnd
      ? moment(endDate || selectedActivationPeriod.activationEnd)
      : null;
  if (!current) return false;
  // Compare at 'day' level for date picker - time restrictions are handled by disableActivationStartTime
  if (minDate && current.isBefore(minDate, 'day')) return true;
  if (maxDate && current.isAfter(maxDate, 'day')) return true;
  return false;
};

export const disableActivationEndDate = (
  current: Moment | undefined,
  activationEndDateLimit: ActivationLimit,
  selectedActivationPeriod: ActivationPeriod,
  startDate: Moment | null,
) => {
  const minDate =
    startDate || selectedActivationPeriod?.activationStart
      ? moment(startDate || selectedActivationPeriod.activationStart)
      : null;
  const maxDate = activationEndDateLimit.date ? moment(activationEndDateLimit.date) : null;
  if (!current) return false;
  // Compare at 'day' level for date picker - time restrictions are handled by disableActivationEndTime
  if (minDate && current.isBefore(minDate, 'day')) return true;
  if (maxDate && current.isAfter(maxDate, 'day')) return true;
  return false;
};

export const disableActivationStartTime = (
  current: Moment | undefined,
  activationStartDateLimit: ActivationLimit,
  selectedActivationPeriod: ActivationPeriod,
  endDate: Moment | null,
) => {
  const minDate = activationStartDateLimit.date
    ? moment(activationStartDateLimit.date).add(1, 'minute')
    : null;
  const maxDate =
    endDate || selectedActivationPeriod?.activationEnd
      ? moment(endDate || selectedActivationPeriod.activationEnd).subtract(1, 'minute')
      : null;
  let disabledHours: number[] = [];
  let disabledMinutes: number[] = [];
  if (current) {
    if (minDate && current.isSame(minDate, 'day')) {
      const minHour = minDate.hour();
      disabledHours = Array.from({ length: minHour }, (_, i) => i);
      if (current.hour() === minHour) {
        const minMinute = minDate.minute();
        disabledMinutes = Array.from({ length: minMinute }, (_, i) => i);
      }
    }
    if (maxDate && current.isSame(maxDate, 'day')) {
      const maxHour = maxDate.hour();
      disabledHours = disabledHours.concat(
        Array.from({ length: 24 - maxHour - 1 }, (_, i) => maxHour + 1 + i),
      );
      if (current.hour() === maxHour) {
        const maxMinute = maxDate.minute();
        disabledMinutes = disabledMinutes.concat(
          Array.from({ length: 60 - maxMinute - 1 }, (_, i) => maxMinute + 1 + i),
        );
      }
    }
  }
  return {
    disabledHours: (): number[] => disabledHours,
    disabledMinutes: (): number[] => disabledMinutes,
  };
};

export const disableActivationEndTime = (
  current: Moment | undefined,
  activationEndDateLimit: ActivationLimit,
  selectedActivationPeriod: ActivationPeriod,
  startDate: Moment | null,
) => {
  const minDate =
    startDate || selectedActivationPeriod?.activationStart
      ? moment(startDate || selectedActivationPeriod.activationStart).add(1, 'minute')
      : null;
  const maxDate = activationEndDateLimit.date
    ? moment(activationEndDateLimit.date).subtract(1, 'minute')
    : null;
  let disabledHours: number[] = [];
  let disabledMinutes: number[] = [];
  if (current) {
    if (minDate && current.isSame(minDate, 'day')) {
      const minHour = minDate.hour();
      disabledHours = Array.from({ length: minHour }, (_, i) => i);
      if (current.hour() === minHour) {
        const minMinute = minDate.minute();
        disabledMinutes = Array.from({ length: minMinute }, (_, i) => i);
      }
    }
    if (maxDate && current.isSame(maxDate, 'day')) {
      const maxHour = maxDate.hour();
      disabledHours = disabledHours.concat(
        Array.from({ length: 24 - maxHour - 1 }, (_, i) => maxHour + 1 + i),
      );
      if (current.hour() === maxHour) {
        const maxMinute = maxDate.minute();
        disabledMinutes = disabledMinutes.concat(
          Array.from({ length: 60 - maxMinute - 1 }, (_, i) => maxMinute + 1 + i),
        );
      }
    }
  }
  return {
    disabledHours: (): number[] => disabledHours,
    disabledMinutes: (): number[] => disabledMinutes,
  };
};
